import {
  DropDownList,
  DropDownListFilterChangeEvent,
  ListItemProps,
} from '@progress/kendo-react-dropdowns';
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import {
  FC,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import { Keys } from 'src/constants/keys';
import useBoolean from 'src/hooks/useBoolean';
import { OfflineSupplier } from 'src/types/catalog/Supplier';
import { stopPropagation } from 'src/utils';

interface SupplierDropDownProps {
  data: OfflineSupplier[];
  onChange: (selectedSupplierIds: number[]) => void;
  isLoading: boolean;
}
const SupplierDropDown: FC<SupplierDropDownProps> = ({ data, onChange, isLoading }) => {
  const dropDownRef = useRef<DropDownList>(null);
  // Maintain full list of all suppliers with checked states
  const [allSuppliers, setAllSuppliers] = useState<OfflineSupplier[]>(data);
  // Filtered suppliers for display only
  const [filteredSuppliers, setFilteredSuppliers] = useState<OfflineSupplier[]>(data);
  const [filterValue, setFilterValue] = useState<string>('');
  // Track initial state for change detection
  const [initialSupplierState, setInitialSupplierState] = useState<OfflineSupplier[]>(data);

  const isAllSelected = useMemo(
    () =>
      filteredSuppliers.length > 0 &&
      filteredSuppliers.every((item: OfflineSupplier) => item.checked),
    [filteredSuppliers]
  );
  const isPartialSelected = useMemo(() => {
    const checkedCount = filteredSuppliers.filter((item: OfflineSupplier) => item.checked).length;
    return checkedCount > 0 && checkedCount < filteredSuppliers.length;
  }, [filteredSuppliers]);
  const { t } = useTranslation();
  const { value: open, setTrue: openDropdown, setFalse: closeDropdown } = useBoolean();
  const value = useMemo(() => {
    const allCheckedCount = allSuppliers.filter((s) => s.checked).length;
    if (allCheckedCount === allSuppliers.length && allSuppliers.length > 0) {
      return t('All Suppliers');
    }

    return allCheckedCount > 0 ? `${allCheckedCount} ${t('selected')}` : t('Select Supplier');
  }, [allSuppliers, t]);

  // Sync with data prop only when dropdown is closed (filter text and filtered list are preserved; filteredSuppliers is derived from allSuppliers + filterValue in the effect below)
  useEffect(() => {
    if (!open) {
      setAllSuppliers(data);
      setInitialSupplierState(data);
    }
  }, [data, open]);

  const handleFilterChange = useCallback((e: DropDownListFilterChangeEvent) => {
    const newFilterValue = e.filter.value || '';
    setFilterValue(newFilterValue);
  }, []);

  // Update filtered suppliers when allSuppliers or filterValue changes
  useEffect(() => {
    if (filterValue.trim() === '') {
      setFilteredSuppliers(allSuppliers);
    } else {
      const filtered = allSuppliers.filter((s) =>
        s.supplierCompanyName.toLowerCase().includes(filterValue.toLowerCase())
      );
      setFilteredSuppliers(filtered);
    }
  }, [allSuppliers, filterValue]);

  // Set indeterminate state on the "Select All" checkbox
  useEffect(() => {
    if (open) {
      // Use setTimeout to ensure DOM is fully rendered
      const timeoutId = setTimeout(() => {
        // Find the checkbox input element in the header
        const popupElement = document.getElementsByClassName('supplierPopup')[0];
        if (popupElement) {
          const headerCheckbox = popupElement.querySelector(
            'li.k-list-item input[type="checkbox"]'
          ) as HTMLInputElement;
          if (headerCheckbox) {
            // Set indeterminate: true when partial selection, false otherwise
            headerCheckbox.indeterminate = isPartialSelected;
          }
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [isPartialSelected, open, isAllSelected]);

  const handleSelectAll = useCallback(
    (event: CheckboxChangeEvent) => {
      // If indeterminate, clicking should check all items
      const newCheckedState = isPartialSelected ? true : event.value;
      // Update only the filtered suppliers in the full list
      setAllSuppliers((prev) =>
        prev.map((item) => {
          const isInFilteredList = filteredSuppliers.some(
            (f) => f.offlineSupplierId === item.offlineSupplierId
          );
          return isInFilteredList ? { ...item, checked: newCheckedState } : item;
        })
      );
    },
    [isPartialSelected, filteredSuppliers]
  );

  const onCheckBoxChange = useCallback((offlineSupplierId: string) => {
    setAllSuppliers((prev) =>
      prev.map((item) => ({
        ...item,
        checked:
          item.offlineSupplierId.toString() === offlineSupplierId.toString()
            ? !item.checked
            : item.checked,
      }))
    );
  }, []);

  const handleLabelClick = useCallback(
    (event: MouseEvent<HTMLLabelElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const field = (event.target as HTMLLabelElement).htmlFor;
      onCheckBoxChange(field);
    },
    [onCheckBoxChange]
  );

  const handleClose = useCallback(() => {
    setFilterValue('');
    setAllSuppliers(data);
    setInitialSupplierState(data);
    closeDropdown();
  }, [closeDropdown, data]);

  const handleOpen = useCallback(() => {
    if (open) {
      handleClose();
    } else {
      // Initialize from applied state when opening (filter text and filtered list preserved via effect)
      setAllSuppliers(data);
      setInitialSupplierState(data);
      openDropdown();
    }
  }, [handleClose, open, openDropdown, data]);

  const handleApply = useCallback(() => {
    // When user has searched: send only checked suppliers that match the current filter.
    // When no filter: send all checked supplier IDs.
    const suppliersToApply =
      filterValue.trim() === ''
        ? allSuppliers.filter((s) => s.checked)
        : allSuppliers.filter(
            (s) => s.checked && filteredSuppliers.some((f) => f.offlineSupplierId === s.offlineSupplierId)
          );
    const checkedSupplierIds = suppliersToApply.map((s) => s.offlineSupplierId) as number[];

    onChange(checkedSupplierIds);
    setFilterValue('');
    closeDropdown();
  }, [closeDropdown, allSuppliers, filteredSuppliers, filterValue, onChange]);

  const itemRender = useCallback(
    (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
      const { dataItem } = itemProps;
      const shouldShowTooltip =
        dataItem.supplierCompanyName && dataItem.supplierCompanyName.length > 20;

      return (
        <li onClick={stopPropagation} className="k-list-item text-[13px]">
          <Checkbox
            type="checkbox"
            id={dataItem.offlineSupplierId.toString()}
            name={dataItem.supplierCompanyName}
            checked={dataItem.checked}
            onChange={() => {
              onCheckBoxChange(dataItem.offlineSupplierId.toString());
            }}
          >
            <label
              htmlFor={dataItem.offlineSupplierId.toString()}
              className={'k-checkbox-label'}
              onClick={handleLabelClick}
              {...(shouldShowTooltip
                ? {
                    'data-tooltip-id': 'global-tooltip',
                    'data-tooltip-content': dataItem.supplierCompanyName,
                  }
                : {})}
            >
              {dataItem.supplierCompanyName}
            </label>
          </Checkbox>
        </li>
      );
    },
    [handleLabelClick, onCheckBoxChange]
  );

  // Check if current state matches initial state (no changes made)
  const hasChanges = useMemo(
    () =>
      allSuppliers.some((item) => {
        const initialItem = initialSupplierState.find(
          (init) => init.offlineSupplierId === item.offlineSupplierId
        );
        return initialItem?.checked !== item.checked;
      }),
    [allSuppliers, initialSupplierState]
  );

  // Determine if Apply button should be disabled
  const hasCheckedInFiltered = useMemo(
    () => filteredSuppliers.some((s) => s.checked),
    [filteredSuppliers]
  );
  const isApplyDisabled = useMemo(() => {
    // Disable if no filtered results (nothing to apply)
    if (filteredSuppliers.length === 0) return true;
    // Disable when no supplier is selected (all unselected)
    if (!hasCheckedInFiltered) return true;
    // Enable when user has made changes or when there is at least one checked supplier in the (filtered) list
    if (hasChanges || hasCheckedInFiltered) return false;
    // Disable if all visible are selected and no changes (nothing new to apply)
    if (isAllSelected && !hasChanges) return true;
    return false;
  }, [filteredSuppliers.length, isAllSelected, hasChanges, hasCheckedInFiltered]);

  const header = useMemo(
    () => (
      <li onClick={stopPropagation} className="k-list-item text-[13px] p-2">
        <Checkbox
          type="checkbox"
          checked={isAllSelected}
          onChange={handleSelectAll}
          label={t('Select All')}
        />
      </li>
    ),
    [handleSelectAll, isAllSelected, t]
  );
  const footer = useMemo(
    () => (
      <div onClick={stopPropagation} className="flex item-center justify-end gap-1">
        <Button onClick={handleApply} size="md" color="primary" disabled={isApplyDisabled}>
          {t('Apply')}
        </Button>
        <Button onClick={handleClose} size="md" color="error">
          {t('Cancel')}
        </Button>
      </div>
    ),
    [handleApply, handleClose, isApplyDisabled, t]
  );

  const handleClickOutside = useCallback(
    (event: any) => {
      const popupRef = document.getElementsByClassName('supplierPopup')[0];
      if (popupRef && !popupRef.contains(event.target)) {
        if (dropDownRef.current?.element && !dropDownRef.current.element.contains(event.target)) {
          handleClose();
        }
      }
    },
    [handleClose]
  );

  useEffect(() => {
    // Use capture phase to catch clicks before stopPropagation() prevents bubbling
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [handleClickOutside]);

  const handleTabKeyPress = useCallback((event: KeyboardEvent<HTMLSpanElement>) => {
    // when user press tab key trap focus
    const modalElement = document.getElementsByClassName('supplierPopup')[0] as HTMLElement;
    if (!modalElement) return;
    const focusableElements = modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ) as NodeListOf<HTMLElement>;
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLSpanElement>) => {
      if (event.code === Keys.tab) {
        handleTabKeyPress(event);
      } else if (event.code === Keys.space) {
        openDropdown();
      } else if (event.code === Keys.enter) {
        handleOpen();
      }
      if (event.key === Keys.esc) {
        handleClose();
      }
    },
    [handleClose, handleOpen, openDropdown, handleTabKeyPress]
  );

  return (
    <span
      onClick={handleOpen}
      className="contents"
      onKeyDown={handleKeyDown}
      {...(value && value.length > 20
        ? {
            'data-tooltip-id': 'global-tooltip',
            'data-tooltip-content': value,
          }
        : {})}
    >
      <DropDownList
        data={filteredSuppliers}
        itemRender={itemRender}
        header={filteredSuppliers.length > 0 ? header : undefined}
        footer={footer}
        value={value}
        opened={open}
        filterable
        filter={filterValue}
        onFilterChange={handleFilterChange}
        popupSettings={{ className: 'supplierPopup' }}
        ref={dropDownRef}
        loading={isLoading}
      />
    </span>
  );
};

export default SupplierDropDown;
